import { useTranslation } from 'react-i18next';
import { UndoOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Form } from 'antd';
import AlertBox from '@/components/alert-box';
import { Dialog, DialogTitle } from '@/components/dialog/Dialog';
import { ROLES_PERMISSIONS } from '@/constants';
import { useNotify, useToggle } from '@/hooks';
import PrivateAction from '@/routes/PrivateAction';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import { getUserFullName } from '@/utils/utils';

/**
 * Brings a suspended employee back to active. Only the status is sent: the
 * server refuses any other change to a suspended employee.
 */
export default function EmployeeRestoreModal({
  record,
  onClose,
  buttonClassName,
}) {
  const { t } = useTranslation(['forms', 'messages']);

  const label = t('restoreEmployee', { ns: 'forms' });
  const notification = useNotify();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { open, onToggle, onClose: handleCancel } = useToggle(false);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () =>
      API.employees.updateEmployeeById(record._id, { status: 'ACTIVE' }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.employees] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.employee, record._id],
      });

      handleCancel();
      onClose?.();

      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
      });
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.response?.data?.message || error.message,
      });
    },
  });

  return (
    <PrivateAction {...ROLES_PERMISSIONS.EMPLOYEES.EDIT}>
      <Dialog
        title={label}
        open={open}
        onCancel={handleCancel}
        form={form}
        confirmLoading={isPending}
        okButtonProps={{ shape: 'round' }}
        cancelButtonProps={{ shape: 'round' }}
        triggerChildren={
          <Button
            shape="round"
            loading={isPending}
            disabled={isPending}
            onClick={() => onToggle(!open)}
            title={label}
            className={buttonClassName}
            icon={<UndoOutlined />}
          >
            <span>{label}</span>
          </Button>
        }
      >
        <Form
          form={form}
          onFinish={() => mutateAsync()}
          layout="vertical"
          name="employee_restore_form"
        >
          <AlertBox variant="warning" className="p-2 text-center">
            <span>{t('restoreEmployeeInfo', { ns: 'forms' })}</span>
          </AlertBox>
          <DialogTitle
            text={t('restoreEmployeeConfirm', { ns: 'forms' })}
            dynamicText={getUserFullName(record?.firstName, record?.lastName)}
          />
        </Form>
      </Dialog>
    </PrivateAction>
  );
}
